import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, PackageCheck, ScanLine, ClipboardCheck, FileCheck, Printer, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import apiClient from '@/services/apiClient';

const StageCard = ({ title, icon: Icon, tone, rows, columns, testid }) => (
  <div className={`rounded-xl border overflow-hidden ${tone.border} ${tone.bg}`} data-testid={testid}>
    <div className={`px-4 py-3 flex items-center justify-between ${tone.head}`}>
      <div className="flex items-center gap-2 font-semibold">
        <Icon className="w-4 h-4" /> {title}
      </div>
      <Badge className={tone.badge}>{rows.length}</Badge>
    </div>
    <div className="max-h-[46vh] overflow-y-auto divide-y divide-black/5">
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-400">None</div>
      ) : rows.map((r) => (
        <div key={r.pick_slip_number} className="px-4 py-2.5 text-sm" data-testid={`ppb-row-${r.pick_slip_number}`}>
          <div className="flex items-center justify-between">
            <span className="font-mono font-medium text-gray-800">{r.pick_slip_number}</span>
            <div className="flex items-center gap-1">
              {r.combined
                ? <Badge variant="outline" className="text-[10px] border-fuchsia-400 text-fuchsia-600">new</Badge>
                : <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-400">old</Badge>}
              {r.is_seasonal && <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-600">seasonal</Badge>}
            </div>
          </div>
          <div className="text-xs text-gray-500 truncate">{r.customer}{r.picker ? ` · ${r.picker}` : ''}</div>
          <div className="text-xs text-gray-600 mt-0.5 flex gap-3">
            {columns.includes('confirm') && <span>Confirmed {r.confirmed_qty}/{r.required_qty} · items {r.items_confirmed}/{r.total_items}</span>}
            {columns.includes('pack') && <span>Packed {r.packed_qty}/{r.confirmed_qty} · {r.boxes} carton(s)</span>}
            {columns.includes('dc') && <span className="text-purple-600">{r.dc_number ? `DC ${r.dc_number}` : 'awaiting DC'}</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PickPackLiveBoard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/wms/pick-and-pack/dashboard');
      setData(res.data);
    } catch (e) {
      // keep prior data on transient errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (auto) { timer.current = setInterval(load, 15000); }
    return () => timer.current && clearInterval(timer.current);
  }, [auto, load]);

  const counts = data?.counts || {};

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate('/wms/fg/packing')} data-testid="ppb-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-fuchsia-400" />
              <div>
                <h1 className="text-lg font-bold">Pick-Pack Live</h1>
                <p className="text-xs text-gray-300">Real-time pick &amp; pack board</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant={auto ? 'default' : 'outline'} size="sm" className={auto ? 'bg-emerald-600 hover:bg-emerald-700' : 'text-white border-white/30'} onClick={() => setAuto((a) => !a)} data-testid="ppb-auto-toggle">
              {auto ? 'Live' : 'Paused'}
            </Button>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={load} data-testid="ppb-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: 'picking', label: 'Picking', tone: 'text-blue-600' },
            { k: 'packing', label: 'Packing', tone: 'text-emerald-600' },
            { k: 'waiting_dc', label: 'Awaiting DC', tone: 'text-purple-600' },
            { k: 'waiting_print', label: 'Ready / DC-linked', tone: 'text-amber-600' },
          ].map((s) => (
            <div key={s.k} className="bg-white rounded-xl border border-[#E5DFD0] p-4" data-testid={`ppb-stat-${s.k}`}>
              <div className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</div>
              <div className={`text-3xl font-black ${s.tone}`}>{counts[s.k] ?? 0}</div>
            </div>
          ))}
        </div>

        {/* stage columns */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <StageCard title="Picking" icon={ClipboardCheck} testid="ppb-stage-picking"
            tone={{ border: 'border-blue-200', bg: 'bg-white', head: 'bg-blue-50 text-blue-800', badge: 'bg-blue-600' }}
            rows={data?.picking || []} columns={['confirm']} />
          <StageCard title="Packing" icon={ScanLine} testid="ppb-stage-packing"
            tone={{ border: 'border-emerald-200', bg: 'bg-white', head: 'bg-emerald-50 text-emerald-800', badge: 'bg-emerald-600' }}
            rows={data?.packing || []} columns={['pack']} />
          <StageCard title="Awaiting DC / transport" icon={FileCheck} testid="ppb-stage-waiting-dc"
            tone={{ border: 'border-purple-200', bg: 'bg-white', head: 'bg-purple-50 text-purple-800', badge: 'bg-purple-600' }}
            rows={data?.waiting_dc || []} columns={['pack', 'dc']} />
          <StageCard title="Ready / batch print" icon={Printer} testid="ppb-stage-waiting-print"
            tone={{ border: 'border-amber-200', bg: 'bg-white', head: 'bg-amber-50 text-amber-800', badge: 'bg-amber-600' }}
            rows={data?.waiting_print || []} columns={['pack', 'dc']} />
        </div>

        {/* throughput */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'pick_throughput', label: 'Pick share today', testid: 'ppb-pick-throughput' },
            { key: 'pack_throughput', label: 'Pack share today', testid: 'ppb-pack-throughput' },
          ].map((blk) => (
            <div key={blk.key} className="bg-white rounded-xl border border-[#E5DFD0] overflow-hidden" data-testid={blk.testid}>
              <div className="px-4 py-3 bg-[#F8F4EE] border-b border-[#E5DFD0] flex items-center gap-2 font-semibold text-[#1A1F2E]">
                <Users className="w-4 h-4" /> {blk.label}
              </div>
              <div className="max-h-[32vh] overflow-y-auto divide-y divide-[#F0EADF]">
                {(data?.[blk.key] || []).length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-gray-400">No activity yet today</div>
                ) : (data?.[blk.key] || []).map((o) => (
                  <div key={o.name} className="px-4 py-2 flex items-center justify-between text-sm">
                    <span className="text-gray-700">{o.name}</span>
                    <span className="font-semibold text-[#2D5F4F]">{o.pieces} pcs</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default PickPackLiveBoard;
